import {
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    OnDestroy,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
    CompanyController,
    DocumentEntity,
    DocumentItemPartial,
    DocumentItemTypeEnum,
    OrderEntity,
} from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    catchError,
    debounceTime,
    firstValueFrom,
    from,
    merge,
    of,
    Subject,
    Subscription,
    switchMap,
    timer,
} from 'rxjs';
import { PdfPrintService } from '../../services/pdf-print.service';
import { DocumentPdfController } from 'src/app/domain/controllers/document/document-pdf.controller';
import { OrderController } from 'src/app/domain/controllers/order.controller';
import {
    normalizeSourceClient,
    normalizeSourceCompany,
    normalizeSourceDocument,
    normalizeSourceOrder,
} from '../../../../pdf-renderer/normalize';
import { effectiveSectionPageBreak } from '../../../../pdf-renderer/document-renderer';
import type { SourceCompany, SourceDocument } from '../../../../pdf-renderer/types';
import { PdfPreviewInteractions } from '../../../pdf-preview/pdf-preview-composer';
import { buildPreviewInteractions, SETTINGS_TYPES } from './preview-interactions';
import { PREVIEW_TOP_ITEM, PreviewHighlight } from '../../services/_edit-data.service';
import {} from '../../../../pdf-renderer/video-qr';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaToastService } from '@shared/components';
import { RaEditTooltipDirective } from '../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { CoverTemplateController } from 'src/app/domain/controllers/cover-template.controller';

import { CoverFragmentBuilderService } from '../../../pdf-preview/cover-fragment-builder.service';
import { PdfPreviewBuilderService } from '../../../pdf-preview/pdf-preview-builder.service';
import { EditDataService } from '../../services/_edit-data.service';
import { EditItemsService } from '../../services/edit-items.service';
import { MediaLookPanelService } from '../../services/media-look-panel.service';
import { EditService } from '../../services/edit-service';

/** Buffer de renderização: 'a' e 'b' se alternam a cada repaginação (ver _swap). */
type PreviewSlot = 'a' | 'b';

/**
 * Onde o usuário estava olhando, em termos de FOLHA — não de pixel.
 *
 * O pixel não sobrevive à repaginação: uma linha a mais lá em cima empurra o documento
 * inteiro, e o zoom do fit() muda a régua. O par (índice da folha, fração dentro dela) é
 * o que a pessoa quer dizer com "eu estava na página 11". `y` é o plano B para o render
 * que caiu no fallback sem paginação.
 */
interface PreviewScrollAnchor {
    page: number;
    ratio: number;
    y: number;
}

/**
 * Painel lateral "Prévia do PDF" — fase B: renderização 100% LOCAL.
 *
 * O HTML vem do renderer TS canônico (pdf-renderer/, byte-idêntico ao assembler C# do engine
 * pelo harness de paridade) e o paged.js pagina no iframe. NENHUMA chamada ao Cloud Run: o
 * engine só entra quando o usuário clica em gerar o PDF. A única ida à rede aqui é o GET do
 * order (uma vez, cacheado — os tokens $ORDER_* precisam dele) e o download das próprias
 * imagens pelo browser.
 *
 * A fonte do refresh é o signal `document()` do EditDataService — cobre edição local otimista
 * E os ecos de Pusher de outros usuários. O fingerprint descarta campos que não mudam o PDF.
 */
@Component({
    selector: 'pdf-preview-panel',
    templateUrl: './pdf-preview-panel.component.html',
    styleUrl: './pdf-preview-panel.component.scss',
    imports: [TranslatePipe, RaIconComponent, RaEditTooltipDirective],
})
export class PdfPreviewPanelComponent implements OnDestroy {
    // Quieto por 1s após a última mudança (mesmo tempo do painel da capa). Sem rede no
    // caminho, o custo de um refresh é só repaginar o iframe.
    private static readonly REFRESH_DEBOUNCE_MS = 1000;

    /** Painel fechado: a prévia só alimenta os traços de quebra, pode esperar mais. */
    private static readonly BACKGROUND_DEBOUNCE_MS = 2500;

    /**
     * Mudança discreta (um clique: tamanho da imagem, quebra de página): só o bastante para
     * juntar cliques seguidos. Esperar o 1s da digitação aqui lia como travamento.
     */
    private static readonly DISCRETE_DEBOUNCE_MS = 150;

    /**
     * Teto do indicador de recálculo. O normal é o iframe avisar que terminou (inclusive
     * quando falha), mas se nenhuma mensagem chegar, o spinner no header não pode girar
     * para sempre: é melhor parar de avisar do que mentir que ainda está trabalhando.
     */
    private static readonly BUSY_TIMEOUT_MS = 30000;

    /**
     * Teto da troca de buffer: se o render novo nunca avisar que terminou (script morto
     * dentro do iframe), a folha antiga não pode ficar na tela para sempre — a contagem
     * de páginas e os traços de quebra já estariam mentindo.
     */
    private static readonly SWAP_TIMEOUT_MS = 30000;

    /**
     * "Encontrar no preview" com a prévia recém-aberta: o primeiro render pode não ter
     * terminado, e sem folha não há o que rolar. Cinco segundos de tentativa cobrem o
     * render inicial sem deixar um pedido velho perseguindo o usuário.
     */
    private static readonly LOCATE_RETRY_MS = 250;
    private static readonly LOCATE_MAX_ATTEMPTS = 20;
    /**
     * A coluna da prévia abre animando a largura (0.25s, edit.page.scss) e a folha refaz o
     * zoom a cada passo. Rolar até um item nesse meio calcula o destino num zoom que já
     * mudou quando a rolagem termina: a folha parava noutro item. Espera a coluna assentar.
     */
    private static readonly OPEN_SETTLE_MS = 320;

    /** Tetos do primeiro render em ociosidade (com e sem requestIdleCallback). */
    private static readonly IDLE_TIMEOUT_MS = 3000;
    private static readonly IDLE_FALLBACK_MS = 1200;

    /**
     * Folha reservada, usada só ENQUANTO a capa de verdade não chega (ver
     * `.preview-cover-pending` no composer). Vale também quando o template não pôde ser
     * buscado: a folha da capa continua contando na paginação, que é o que faz os números do
     * sumário baterem com o PDF.
     */
    private static readonly COVER_PLACEHOLDER_HTML =
        '<div class="container-cover preview-cover-pending"></div>';

    // * [Injects]
    public editDataService = inject(EditDataService);
    private _companyController = inject(CompanyController);
    private _orderController = inject(OrderController);
    private _sanitizer = inject(DomSanitizer);
    private _documentPdfController = inject(DocumentPdfController);
    private _pdfPrintService = inject(PdfPrintService);
    private _previewBuilder = inject(PdfPreviewBuilderService);
    private _coverFragmentBuilder = inject(CoverFragmentBuilderService);
    private _coverTemplateController = inject(CoverTemplateController);
    private _editItemsService = inject(EditItemsService);
    private _mediaPanel = inject(MediaLookPanelService);
    private _translate = inject(TranslateService);
    private _raToastService = inject(RaToastService);
    private _editService = inject(EditService);
    private _host = inject<ElementRef<HTMLElement>>(ElementRef);

    /**
     * Modo "Editar com IA": a prévia é metade da tela e fica sempre aberta, então saem o
     * expandir e o fechar. O cabeçalho e o palco aceitam conteúdo projetado do modo
     * (`previewHeaderNote`, `previewOverlay`).
     */
    public chatMode = input(false);

    // * [View children]
    // Dois iframes empilhados (ver _swap): um na tela, o outro paginando escondido.
    private readonly _frameA = viewChild<ElementRef<HTMLIFrameElement>>('frameA');
    private readonly _frameB = viewChild<ElementRef<HTMLIFrameElement>>('frameB');

    // * [State]
    public loading = signal(false);

    /** Níveis do zoom da prévia (1 = folha na largura do painel). */
    public static readonly ZOOM_LEVELS = [0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2];
    private static readonly ZOOM_STORAGE_KEY = 'pdfPreviewZoom';
    /** Zoom escolhido pela pessoa (+ e -), lembrado entre as visitas. */
    public zoom = signal(PdfPreviewPanelComponent._storedZoom());
    public zoomPercent = computed(() => Math.round(this.zoom() * 100));
    public canZoomIn = computed(() => this.zoom() < PdfPreviewPanelComponent.ZOOM_LEVELS.at(-1)!);
    public canZoomOut = computed(() => this.zoom() > PdfPreviewPanelComponent.ZOOM_LEVELS[0]);
    public error = signal(false);
    public srcdocA = signal<SafeHtml | null>(null);
    public srcdocB = signal<SafeHtml | null>(null);
    /** Qual buffer está na tela; `null` enquanto a primeira folha não ficou pronta. */
    public activeSlot = signal<PreviewSlot | null>(null);
    /** Relato do iframe: paginou, caiu no fallback sem quebras, ou quebrou. */
    public renderStatus = signal<'idle' | 'paginating' | 'ok' | 'unpaged' | 'partial' | 'failed'>(
        'idle'
    );
    public renderDetail = signal<string | null>(null);
    /** Entre o clique e a janela de impressão há um round-trip da cobrança. */
    public printing = signal(false);
    public pageCount = signal(0);

    private readonly _refresh$ = new Subject<'typing' | 'discrete'>();
    private readonly _immediate$ = new Subject<void>();
    private _subscription: Subscription;
    private _printSubscription: Subscription;
    private _lastFingerprint: string | null = null;
    private _busyTimeout?: ReturnType<typeof setTimeout>;
    /** Buffer que está paginando fora da tela, à espera da troca. */
    private _pendingSlot: PreviewSlot | null = null;
    /**
     * Item que a folha ainda não achou enquanto um render novo estava em voo (a IA acabou
     * de criar a seção e a prévia ainda repagina): vale de novo assim que a folha trocar.
     */
    private _pendingLocate: string | null = null;
    private _swapTimeout?: ReturnType<typeof setTimeout>;
    /** Quando a prévia abriu por último (ver OPEN_SETTLE_MS). */
    private _openedAt = 0;

    // Caches por vida do painel: order por id, dimensões por URL, QR por conteúdo.
    private readonly _orderCache = new Map<string, OrderEntity | null>();

    /*
     * Capa já montada, guardada pela "cara" da capa do documento.
     *
     * O painel remonta a prévia a cada tecla digitada; buscar o template e recolorir o SVG do
     * fundo em toda remontagem seria uma ida ao servidor por tecla. A chave é o partial `cover`
     * do documento, então mexer na capa (e só isso) refaz o fragmento.
     */
    private _coverCacheKey: string | null = null;
    private _coverHtml: string | null = null;

    constructor() {
        // A paginação roda dentro do iframe; sem este canal, uma falha lá vira só um painel
        // esquisito, sem pista nenhuma de diagnóstico.
        window.addEventListener('message', this._onFrameMessage);

        // Modo "Editar com IA": o realce dos blocos que a IA mexe mora DENTRO da folha. Cada
        // repaginação troca o iframe (buffer duplo), então o realce é reenviado a cada troca.
        effect(() => {
            const highlight = this.editDataService.previewHighlight();
            this.activeSlot();
            untracked(() => this._postHighlight(highlight));
        });

        // "Gerar PDF" no header imprime esta folha. O painel pode estar FECHADO — ele
        // continua montado e paginando em segundo plano, então a folha existe de qualquer
        // jeito e não é preciso abrir o painel para imprimir.
        this._printSubscription = this._pdfPrintService.requests$.subscribe(() => this.download());

        this._subscription = merge(
            // Debounce maior com o painel fechado: aí a prévia só alimenta os traços de
            // quebra de página, e ninguém está olhando a folha repaginar.
            this._refresh$.pipe(
                debounceTime(0),
                switchMap(kind =>
                    timer(
                        kind === 'discrete'
                            ? PdfPreviewPanelComponent.DISCRETE_DEBOUNCE_MS
                            : this.editDataService.pdfPreviewOpen()
                              ? PdfPreviewPanelComponent.REFRESH_DEBOUNCE_MS
                              : PdfPreviewPanelComponent.BACKGROUND_DEBOUNCE_MS
                    )
                )
            ),
            this._immediate$
        )
            .pipe(
                switchMap(() => {
                    this.loading.set(true);
                    this._markBusy(true);
                    this.error.set(false);
                    this.renderStatus.set('idle');
                    this.renderDetail.set(null);
                    return from(this._buildSrcdoc()).pipe(
                        catchError(err => {
                            this.renderDetail.set(String(err?.message ?? err));
                            return of(null);
                        })
                    );
                })
            )
            .subscribe(srcdoc => {
                this.loading.set(false);

                if (srcdoc === null) {
                    // Mantém a última folha boa na tela; só sinaliza o erro.
                    this.error.set(true);
                    this._markBusy(false);
                    return;
                }

                // Vai SEMPRE para o buffer de trás: a folha que o usuário está lendo
                // continua na tela, com o scroll dela, até o novo render terminar.
                const target: PreviewSlot = this.activeSlot() === 'a' ? 'b' : 'a';
                this._pendingSlot = target;
                this._srcdocOf(target).set(this._sanitizer.bypassSecurityTrustHtml(srcdoc));

                clearTimeout(this._swapTimeout);
                this._swapTimeout = setTimeout(
                    () => this._swap(target),
                    PdfPreviewPanelComponent.SWAP_TIMEOUT_MS
                );
            });

        effect(() => {
            if (this.editDataService.pdfPreviewOpen()) this._openedAt = Date.now();
        });

        // "Encontrar no preview": o editor pede, a folha rola. O pedido é consumido na hora
        // (um id só vale uma vez), e a entrega em si tolera a prévia ainda estar paginando.
        effect(() => {
            const itemId = this.editDataService.previewScrollRequestId();
            if (!itemId) return;

            untracked(() => {
                this.editDataService.clearPreviewScrollRequest();
                this._locateInPreview(itemId);
            });
        });

        // Toda mutação do documento (local otimista ou eco de Pusher) reavalia o fingerprint.
        effect(() => {
            const document = this.editDataService.document();
            if (!document) return;

            // O modo entra na assinatura: trocar de modo (manual ⇄ IA) remonta a folha,
            // com ou sem os controles de item (ver _interactionsFor).
            const fingerprint = `${this._fingerprint(document)}|${this.chatMode() ? 'ai' : 'edit'}`;

            untracked(() => {
                // Consumida SEMPRE, mesmo sem mudança de fingerprint: uma marca sobrando
                // apressaria a digitação seguinte.
                const discrete = this.editDataService.takeDiscretePreviewChange();
                if (fingerprint === this._lastFingerprint) return;

                const isFirstLoad = this._lastFingerprint === null;
                this._lastFingerprint = fingerprint;

                // A primeira prévia acontece assim que o editor abre, para o painel já
                // nascer pronto e os traços de quebra existirem desde o começo. Vai para
                // a ociosidade: montar o HTML é rápido, mas medir as imagens não é, e
                // isso não pode competir com a renderização do editor.
                if (isFirstLoad) this._whenIdle(() => this._immediate$.next());
                else this._refresh$.next(discrete ? 'discrete' : 'typing');
            });
        });
    }

    /** Indicador de recálculo do header, com teto de tempo (ver BUSY_TIMEOUT_MS). */
    private _markBusy(busy: boolean): void {
        clearTimeout(this._busyTimeout);
        this.editDataService.setPdfPreviewBusy(busy);

        if (busy) {
            this._busyTimeout = setTimeout(
                () => this.editDataService.setPdfPreviewBusy(false),
                PdfPreviewPanelComponent.BUSY_TIMEOUT_MS
            );
        }
    }

    public ngOnDestroy(): void {
        // unsubscribe (não complete): cancela também o build em voo do switchMap.
        this._subscription.unsubscribe();
        this._printSubscription.unsubscribe();
        clearTimeout(this._busyTimeout);
        clearTimeout(this._swapTimeout);
        this.editDataService.setPdfPreviewBusy(false);
        window.removeEventListener('message', this._onFrameMessage);
    }

    /** Só mensagens do nosso srcdoc (origin é `null` em iframe srcdoc — filtra pelo marcador). */
    private readonly _onFrameMessage = (event: MessageEvent): void => {
        const data = event.data;
        if (!data || data.source !== 'rapidocs-pdf-preview') return;

        // Ação do usuário NA folha (a seta ao lado do título). Vem sempre do buffer que
        // está na tela, então ela não passa pelo filtro de render em voo abaixo — com uma
        // repaginação a caminho, o clique seria descartado justamente no momento em que o
        // usuário acabou de dar.
        if (data.action) {
            this._runItemAction(String(data.action), String(data.itemId ?? ''));
            return;
        }

        // De qual buffer veio. Com um render novo em voo, quem manda é ele: um erro
        // tardio da folha antiga (uma imagem que morreu, por exemplo) não pode pintar
        // o aviso de falha sobre um render que ainda está de pé.
        const slot = this._slotOf(event.source);
        if (!slot) return;
        if (this._pendingSlot && slot !== this._pendingSlot) return;

        // Começou a paginar (escondido): só o spinner do header conta que há trabalho.
        // A contagem de páginas continua sendo a da folha que está na tela.
        if (data.phase === 'start') {
            this.renderStatus.set('paginating');
            return;
        }

        this.pageCount.set(Number(data.pages) || 0);
        if (data.detail) this.renderDetail.set(String(data.detail));

        if (data.phase === 'rendered') this.renderStatus.set('ok');
        else if (data.phase === 'fallback') this.renderStatus.set('unpaged');
        // Paginação morreu no meio: as folhas na tela são só o começo do documento.
        else if (data.phase === 'partial') this.renderStatus.set('partial');
        else if (data.phase === 'error') this.renderStatus.set('failed');

        // O trabalho só acaba quando o iframe termina de paginar: o srcdoc pronto é
        // metade do caminho, e é a paginação que atualiza os traços de quebra.
        this._markBusy(false);

        // Publica as quebras para a coluna de itens desenhar os traços.
        if (data.breaks) {
            this.editDataService.setPdfPageBreaks(
                data.breaks.startsPage ?? [],
                data.breaks.continuesInto ?? [],
                data.breaks.rowBreaks ?? []
            );
        }

        // 'error' vem do window.onerror do iframe e pode chegar antes de a paginação
        // sequer começar; trocar aí colocaria uma folha em branco na tela. Se for esse
        // o caso, quem troca é o teto de tempo.
        // A folha nova já entra no zoom da pessoa (ela pagina escondida, no ajuste à largura).
        if (data.phase !== 'error') this._postZoom(this._frameWindow(slot));

        if (slot === this._pendingSlot && data.phase !== 'error') this._swap(slot);
    };

    /**
     * Ação escolhida no menu que a prévia mostra ao passar o mouse por um item.
     *
     * A quebra de página é resolvida aqui mesmo, em silêncio: é um ajuste de layout, e
     * arrastar o editor para outra seção por causa dele só atrapalharia. As outras três
     * acontecem NA COLUNA DE ITENS — é lá que mora o card, o painel de formato da mídia
     * (o mesmo do botão do rail) e a remoção com desfazer —, então elas abrem a seção
     * dona antes de qualquer coisa.
     */
    private _runItemAction(action: string, itemId: string): void {
        // Mouse entrando/saindo de um item: o card na coluna de itens acompanha. Chega
        // com itemId vazio quando o mouse sai.
        if (action === 'hover') return this.editDataService.setPreviewHoverItem(itemId || null);

        // Esc apertado com o foco dentro da folha: fecha o painel da mídia, como o Esc de fora.
        if (action === 'escape') return this._mediaPanel.close();

        // Resposta do iframe ao "Encontrar no preview": o item não está na folha. Acontece
        // de verdade — quebra de página não desenha nada, e vídeo com `showVideo` desligado
        // sai do impresso de propósito.
        if (action === 'locate-missing') {
            // Render novo em voo: o item provavelmente está na folha que vem aí. Guarda o
            // pedido para o `_swap` e não incomoda com o aviso.
            if (this._renderInFlight() && itemId) {
                this._pendingLocate = itemId;
                return;
            }
            this._pendingLocate = null;
            this._raToastService.warning({
                message: this._translate.instant('document.edit.cardBase.locateInPreviewMissing'),
            });
            return;
        }

        const owner = this.editDataService.findFieldAnywhere(itemId);
        if (!owner) return;

        // Painel da mídia aberto (modo foco): clicar noutra foto ou vídeo da folha troca o
        // painel para ela, que é o que a pessoa está fazendo ali, ajustar as mídias.
        if (
            action === 'locate' &&
            this._mediaPanel.focused() &&
            SETTINGS_TYPES.includes(owner.field.type)
        ) {
            action = 'settings';
        }

        // Editar, encontrar e remover acontecem no card, que está debaixo do fundo escuro: o
        // painel sai antes. A quebra de página é ajuste da folha e não tira ninguém do foco.
        if (action === 'edit' || action === 'locate' || action === 'remove') this._mediaPanel.close();

        switch (action) {
            case 'start-on-new-page':
                return this._setStartOnNewPage(owner.field, true);
            case 'back-to-previous-page':
                return this._setStartOnNewPage(owner.field, false);
            // Clique no próprio item: só leva até o card (o composer é o lápis).
            case 'locate':
                return this._openInEditor(owner.sessionId, owner.field);
            case 'edit':
                // Texto abre já no composer; mídia para no card (a edição dela é modal
                // própria, escolhida no card).
                return this._openInEditor(
                    owner.sessionId,
                    owner.field,
                    PdfPreviewPanelComponent.TEXT_TYPES.includes(owner.field.type)
                );
            case 'settings':
                // O card dono abre o painel dele, que entra por cima da prévia e a devolve
                // quando fecha (MediaLookPanelService).
                this._openInEditor(owner.sessionId, owner.field);
                return this.editDataService.requestItemConfig(itemId);
            case 'remove':
                this._openInEditor(owner.sessionId, owner.field);
                // O serviço pede confirmação e depois remove com desfazer, igual ao card.
                return this._editItemsService.removeItem([itemId]);
        }
    }

    /**
     * Traz o card para a tela: abre a seção dona, entra na pasta de checklist quando o
     * campo mora dentro de uma, e pede o foco (a coluna de itens rola e destaca).
     *
     * Abrir a seção não é só cosmético: a remoção e a config trabalham sobre a seção ATUAL,
     * então sem isto elas mirariam na seção errada.
     */
    private _openInEditor(sessionId: string, field: DocumentItemPartial, edit = false): void {
        const session = this.editDataService
            .document()
            ?.sessions?.find(item => item._id === sessionId);
        if (session) this.editDataService.setCurrentSession(session);

        this.editDataService.setCurrentFolderId(field.parentFolderId ?? null);
        this.editDataService.requestItemFocus(field._id, edit);
    }

    /** Itens cujo "Editar" abre o composer de texto (os demais só ganham o foco no card). */
    private static readonly TEXT_TYPES: DocumentItemTypeEnum[] = [
        DocumentItemTypeEnum.TITLE,
        DocumentItemTypeEnum.SUBTITLE,
        DocumentItemTypeEnum.TEXT,
    ];

    /**
     * Liga/desliga o "começa em folha nova" de um item, pelo menu da prévia.
     *
     * É o MESMO atributo do card (`configs.pageBreakBefore`), então as duas telas contam a
     * mesma história: a seta para baixo empurra o bloco para a folha seguinte e a de cima
     * o traz de volta, que é o que o "x" da tag de quebra faz no card.
     */
    private _setStartOnNewPage(field: DocumentItemPartial, value: boolean): void {
        // Já está assim: um clique repetido (de uma folha que ainda não repaginou, por
        // exemplo) não pode virar uma escrita à toa.
        if ((field.configs?.pageBreakBefore === true) === value) return;

        this._editItemsService.updateItemConfig(field._id, {
            ...(field.configs ?? {}),
            pageBreakBefore: value,
        });
    }

    /**
     * O que a folha pode oferecer em cada item. As regras moram em
     * `buildPreviewInteractions` (função pura, com testes); aqui ficam só os rótulos e o
     * corte de documento bloqueado.
     */
    private _interactionsFor(
        rawDocument: DocumentEntity,
        document: SourceDocument,
        company: SourceCompany | undefined
    ): PdfPreviewInteractions | undefined {
        // Documento bloqueado (enviado para assinatura) não aceita edição: sem isto, a
        // folha nasce sem controle nenhum.
        if (this.editDataService.documentIsBlocked()) return undefined;

        // Modo IA: a folha é só leitura e acompanhamento. O hover e a barra de ações de
        // cada item são coisa do modo de edição; aqui quem mexe no documento é o chat.
        if (this.chatMode()) return undefined;

        return buildPreviewInteractions(rawDocument, effectiveSectionPageBreak(document, company), {
            edit: this._translate.instant('document.edit.pdfPreview.editItem'),
            startOnNewPage: this._translate.instant('document.edit.cardBase.startOnNewPage'),
            backToPreviousPage: this._translate.instant(
                'document.edit.pdfPreview.backToPreviousPage'
            ),
            settings: this._translate.instant('document.edit.pdfPreview.itemSettings'),
            remove: this._translate.instant('document.edit.cardBase.removeField'),
        });
    }

    /**
     * Roda quando o navegador estiver ocioso, com teto de tempo: em Safari (sem
     * requestIdleCallback) e em abas ocupadas o trabalho não pode ficar esperando para
     * sempre, senão a prévia nunca fica pronta.
     */
    private _whenIdle(work: () => void): void {
        const idle = (window as any).requestIdleCallback as
            ((cb: () => void, opts?: { timeout: number }) => number) | undefined;

        if (idle) idle(work, { timeout: PdfPreviewPanelComponent.IDLE_TIMEOUT_MS });
        else setTimeout(work, PdfPreviewPanelComponent.IDLE_FALLBACK_MS);
    }

    public expanded = this.editDataService.pdfPreviewExpanded;
    public expandLabelKey = computed(() =>
        this.expanded() ? 'document.edit.pdfPreview.collapse' : 'document.edit.pdfPreview.expand'
    );

    public retry(): void {
        this._immediate$.next();
    }

    public close(): void {
        this.editDataService.togglePdfPreviewOpen();
    }

    /**
     * Alarga a coluna (a largura é do grid do editor, não do painel). O zoom da folha é
     * recalculado dentro do iframe pelo listener de resize do composer.
     */
    public toggleExpanded(): void {
        this.editDataService.togglePdfPreviewExpanded();
    }

    /**
     * Gera o PDF imprimindo a PRÓPRIA prévia. É o único caminho: não há render no
     * servidor nem arquivo guardado: a folha que está na tela é o documento, e o
     * "Salvar como PDF" do navegador é quem escreve o arquivo.
     *
     * Sem diálogo de confirmação. Ele existia enquanto isto era uma via alternativa ao
     * PDF do engine e precisava avisar o que se perdia pelo caminho; agora não se perde
     * nada, e um aviso a cada impressão só ficaria no meio.
     */
    public download(): void {
        // Um PDF por vez: um segundo clique durante a cobrança cobraria duas vezes.
        if (this.printing()) return;

        const frame = this._activeFrame();
        if (!frame?.contentWindow) {
            // A primeira paginação ainda não terminou: antes o clique não fazia nada.
            this._raToastService.warning({
                message: this._translate.instant('document.edit.pdfPreview.notReady'),
            });
            return;
        }

        const documentId = this.editDataService.document()?._id;
        if (!documentId) return;

        // Cobra ANTES de imprimir: o arquivo sai do navegador e some do nosso alcance,
        // então não há como cobrar depois pelo tamanho real. Sem saldo, a API responde
        // 402, o controller já mostra o toast e a impressão não acontece. Enquanto a
        // cobrança não volta, o carregamento da tela diz o que está acontecendo.
        this.printing.set(true);
        this._editService.initLoading('document.edit.pdfPreview.preparingPdf');
        this._documentPdfController.chargePreviewDownload(documentId).subscribe({
            next: () => {
                this.printing.set(false);
                this._editService.stopLoading();
                this._printFrame();
            },
            error: () => {
                this.printing.set(false);
                this._editService.stopLoading();
            },
        });
    }

    /**
     * O diálogo de impressão precisa ser o do DOCUMENTO DO IFRAME; sem o focus antes,
     * parte dos navegadores imprime a página do editor.
     */
    private _printFrame(): void {
        const win = this._activeFrame()?.contentWindow;
        if (!win) return;

        win.focus();
        win.print();
    }

    // * ── Buffer duplo ─────────────────────────────────────────────────────────

    /**
     * Coloca na tela a folha que acabou de ser paginada, já rolada até onde o usuário
     * estava.
     *
     * Repaginar recarrega o iframe do zero; com um iframe só, cada tecla digitada jogava
     * a pessoa de volta à capa e ainda piscava o painel em branco no caminho. Por isso o
     * render novo acontece no iframe DE TRÁS (escondido) e a troca só acontece aqui, com
     * a rolagem restaurada ANTES de ele aparecer — quem está lendo a página 11 continua
     * na página 11, sem piscada.
     */
    private _swap(slot: PreviewSlot): void {
        clearTimeout(this._swapTimeout);
        this._pendingSlot = null;

        const previous = this.activeSlot();
        if (previous === slot) return;

        const anchor = previous ? this._captureAnchor(this._frameWindow(previous)) : null;
        if (anchor) this._applyAnchor(this._frameWindow(slot), anchor);

        this.activeSlot.set(slot);

        // A folha antiga sai do DOM: manter dois documentos paginados vivos dobraria a
        // memória do painel à toa (o próximo render nasce num iframe novo mesmo).
        if (previous) this._srcdocOf(previous).set(null);

        // Pedido de rolagem que a folha anterior não conseguiu atender: a nova atende.
        const pending = this._pendingLocate;
        if (pending) {
            this._pendingLocate = null;
            setTimeout(() => this._locateInPreview(pending), 0);
        }
    }

    /** Há um render novo a caminho (montando ou paginando no buffer de trás)? */
    private _renderInFlight(): boolean {
        return this.loading() || this.renderStatus() === 'paginating' || !!this._pendingSlot;
    }

    public zoomIn(): void {
        const next = PdfPreviewPanelComponent.ZOOM_LEVELS.find(level => level > this.zoom() + 0.001);
        if (next) this._setZoom(next);
    }

    public zoomOut(): void {
        const next = [...PdfPreviewPanelComponent.ZOOM_LEVELS].reverse().find(level => level < this.zoom() - 0.001);
        if (next) this._setZoom(next);
    }

    /** Volta para a folha inteira na largura do painel. */
    public resetZoom(): void {
        this._setZoom(1);
    }

    private _setZoom(zoom: number): void {
        this.zoom.set(zoom);
        try {
            localStorage.setItem(PdfPreviewPanelComponent.ZOOM_STORAGE_KEY, String(zoom));
        } catch {
            // Sem storage (aba privada): o zoom vale só nesta visita.
        }
        this._postZoom(this.activeSlot() ? this._activeFrame()?.contentWindow : null);
    }

    /** Aplica o zoom numa folha (a da tela ou a que acabou de paginar escondida). */
    private _postZoom(win: Window | null | undefined): void {
        win?.postMessage({ source: 'rapidocs-editor', action: 'set-zoom', zoom: this.zoom() }, '*');
    }

    private static _storedZoom(): number {
        try {
            const stored = Number(localStorage.getItem(PdfPreviewPanelComponent.ZOOM_STORAGE_KEY));
            return PdfPreviewPanelComponent.ZOOM_LEVELS.includes(stored) ? stored : 1;
        } catch {
            return 1;
        }
    }

    /**
     * Manda o iframe ATIVO procurar o item e rolar até ele.
     *
     * Quando a prévia acabou de ser aberta, o primeiro render ainda pode estar em voo e não
     * existe folha para rolar: em vez de perder o pedido, tenta de novo por alguns segundos.
     * Uma repaginação em curso não atrapalha — quem está na tela é o buffer ativo, e o
     * `_swap` restaura a rolagem dele na folha nova.
     */
    private _locateInPreview(itemId: string, attempt = 0): void {
        const settling = this._openedAt + PdfPreviewPanelComponent.OPEN_SETTLE_MS - Date.now();
        if (settling > 0) {
            setTimeout(() => this._locateInPreview(itemId, attempt), settling);
            return;
        }

        const win = this.activeSlot() ? this._activeFrame()?.contentWindow : null;

        if (!win) {
            if (attempt >= PdfPreviewPanelComponent.LOCATE_MAX_ATTEMPTS) return;
            setTimeout(
                () => this._locateInPreview(itemId, attempt + 1),
                PdfPreviewPanelComponent.LOCATE_RETRY_MS
            );
            return;
        }

        // Topo da folha: destino das alterações sem item (capa, título do documento).
        if (itemId === PREVIEW_TOP_ITEM) {
            win.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        win.postMessage({ source: 'rapidocs-editor', action: 'locate-item', itemId }, '*');
    }

    /**
     * Manda o iframe ATIVO realçar os itens (ou limpar, sem realce). As cores vão junto,
     * lidas dos tokens do editor: a folha é outro documento e não enxerga os tokens.
     */
    private _postHighlight(highlight: PreviewHighlight | null): void {
        const win = this.activeSlot() ? this._activeFrame()?.contentWindow : null;
        if (!win) return;

        const style = getComputedStyle(this._host.nativeElement);
        const token = (name: string) => style.getPropertyValue(name).trim();
        win.postMessage(
            {
                source: 'rapidocs-editor',
                action: 'highlight-items',
                itemIds: highlight?.itemIds ?? [],
                tone: highlight?.tone ?? null,
                marks: highlight?.marks ?? [],
                color: token('--primary'),
                colors: { conflict: token('--accentOrange'), error: token('--accentRed') },
            },
            '*'
        );
    }

    private _captureAnchor(win: Window | null | undefined): PreviewScrollAnchor | null {
        if (!win) return null;

        const y = win.scrollY;
        // No topo não há o que restaurar — e a folha nova já começa no topo.
        if (y <= 0) return null;

        const pages = win.document.querySelectorAll<HTMLElement>('.pagedjs_page');
        let anchor: PreviewScrollAnchor = { page: 0, ratio: 0, y };

        for (let i = 0; i < pages.length; i++) {
            const rect = pages[i].getBoundingClientRect();
            const top = rect.top + y;
            if (top > y + 1) break;

            anchor = { page: i, ratio: rect.height ? (y - top) / rect.height : 0, y };
        }

        return anchor;
    }

    /** Sem folhas (render que caiu no fallback), sobra o pixel salvo. */
    private _applyAnchor(win: Window | null | undefined, anchor: PreviewScrollAnchor): void {
        if (!win) return;

        const pages = win.document.querySelectorAll<HTMLElement>('.pagedjs_page');
        if (!pages.length) {
            win.scrollTo(0, anchor.y);
            return;
        }

        // Documento encolheu: para na última folha em vez de ficar no vazio do fim.
        const rect = pages[Math.min(anchor.page, pages.length - 1)].getBoundingClientRect();
        win.scrollTo(0, Math.max(0, rect.top + win.scrollY + anchor.ratio * rect.height));
    }

    private _srcdocOf(slot: PreviewSlot) {
        return slot === 'a' ? this.srcdocA : this.srcdocB;
    }

    private _frameOf(slot: PreviewSlot): HTMLIFrameElement | undefined {
        return (slot === 'a' ? this._frameA() : this._frameB())?.nativeElement;
    }

    private _frameWindow(slot: PreviewSlot): Window | null | undefined {
        return this._frameOf(slot)?.contentWindow;
    }

    private _activeFrame(): HTMLIFrameElement | undefined {
        const slot = this.activeSlot();
        return slot ? this._frameOf(slot) : undefined;
    }

    private _slotOf(source: MessageEventSource | null): PreviewSlot | null {
        if (!source) return null;
        if (source === this._frameWindow('a')) return 'a';
        if (source === this._frameWindow('b')) return 'b';
        return null;
    }

    // * ── Pipeline local ───────────────────────────────────────────────────────

    private async _buildSrcdoc(): Promise<string | null> {
        const rawDocument = this.editDataService.document();
        if (!rawDocument) return null;

        // Round-trip JSON: entidades viram o mesmo shape que a API entrega (datas como ISO),
        // que é o contrato do normalize — o mesmo caminho validado no harness de paridade.
        const plain = (value: unknown): any =>
            value === null || value === undefined ? undefined : JSON.parse(JSON.stringify(value));

        const document = normalizeSourceDocument(plain(rawDocument));
        const rawCompany = this._companyController.currentCompany();
        const company = rawCompany ? normalizeSourceCompany(plain(rawCompany)) : undefined;
        const rawClient = this.editDataService.client();
        const client = rawClient ? normalizeSourceClient(plain(rawClient)) : undefined;
        const order = await this._resolveOrder(document.orderId);

        return this._previewBuilder.build({
            document,
            company,
            client,
            order,
            renderedCoverHtml: await this._resolveCoverHtml(rawDocument, company),
            // Carimba os itens: é o que liga a paginação real aos traços do editor.
            annotateItemIds: true,
            interactions: this._interactionsFor(rawDocument, document, company),
        });
    }

    /**
     * HTML da capa para esta prévia: o mesmo fragmento que a API monta para o PDF, só que
     * construído aqui (ver CoverFragmentBuilderService).
     *
     * Nunca lança e nunca devolve vazio: sem capa a folha sumiria da paginação e todo número
     * do sumário andaria uma página em relação ao PDF. Falhou, volta a folha reservada.
     */
    private async _resolveCoverHtml(rawDocument: DocumentEntity, company: any): Promise<string> {
        const placeholder = PdfPreviewPanelComponent.COVER_PLACEHOLDER_HTML;

        const showCover =
            rawDocument.config?.showCover ?? company?.documentConfig?.showCover ?? true;
        if (!rawDocument.cover || !showCover) return placeholder;

        // O título entra na chave porque a capa o resolve pelo token `$DOCUMENT_TITLE`:
        // renomear o documento não mexe em `cover`, e sem isto a prévia seguiria mostrando
        // o título antigo na capa enquanto o cabeçalho do editor já mostrava o novo.
        const key = JSON.stringify({ cover: rawDocument.cover, title: rawDocument.title });
        if (key === this._coverCacheKey && this._coverHtml) return this._coverHtml;

        try {
            const template = await firstValueFrom(
                this._coverTemplateController.getById(String(rawDocument._id))
            );
            if (!template) return placeholder;

            const html = await this._coverFragmentBuilder.build(template);
            this._coverCacheKey = key;
            this._coverHtml = html;

            return html;
        } catch {
            return placeholder;
        }
    }

    private async _resolveOrder(orderId: string | undefined): Promise<any> {
        if (!orderId) return undefined;

        if (!this._orderCache.has(orderId)) {
            try {
                const order = await firstValueFrom(this._orderController.getById(orderId));
                this._orderCache.set(orderId, order ?? null);
            } catch {
                this._orderCache.set(orderId, null);
            }
        }

        const cached = this._orderCache.get(orderId);
        return cached ? normalizeSourceOrder(JSON.parse(JSON.stringify(cached))) : undefined;
    }

    /**
     * Fingerprint = documento NORMALIZADO (o input exato do renderer): presença online,
     * updatedAt, foco de seção, status de PDF etc. ficam de fora por construção — o que
     * o normalize não mapeia não pode mudar o preview, então não dispara refresh.
     * (Filtrar por lista de campos voláteis deixava passar ruído do realtime e o painel
     * repaginava sozinho de tempos em tempos.)
     */
    private _fingerprint(document: DocumentEntity): string {
        try {
            return JSON.stringify(normalizeSourceDocument(JSON.parse(JSON.stringify(document))));
        } catch {
            return JSON.stringify(document);
        }
    }
}
